import { PageTitle } from "components/PageTitle";
import { Button } from "components/ui/Button";
import { Link } from "components/ui/Link";
import { Loading } from "components/ui/Loading/Loading";
import { StatusBadge } from "components/ui/StatusBadge";
import { Table } from "components/ui/Table/Table";
import {
  type ImportResult,
  useContactsApi,
  useDeleteAllContactsApi,
  useDeleteContactApi,
  useExportContactsApi,
  useGroupsApi,
  useImportContactsApi,
} from "hooks/use-distribution-api";
import { Download, MoreVertical, Trash2, Upload } from "lucide-react";
import { type FC, useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { DistributionTabs } from "routes/admin/distribution/components/DistributionTabs";

export const ContactsPage: FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(
    undefined,
  );

  const { data, loading, error, refetch } = useContactsApi({
    page,
    limit: 50,
    search: search || undefined,
    groupId: groupFilter || undefined,
    isActive: activeFilter,
  });
  const { data: groupsData } = useGroupsApi({ limit: 100 });
  const { deleteContact } = useDeleteContactApi();

  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // CSVインポート関連
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importGroupId, setImportGroupId] = useState("");
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { importContacts, loading: importLoading } = useImportContactsApi();

  // 全削除関連
  const [deleteAllModalOpen, setDeleteAllModalOpen] = useState(false);
  const { deleteAllContacts, loading: deleteAllLoading } =
    useDeleteAllContactsApi();

  // CSVエクスポート関連
  const { exportContacts, loading: exportLoading } = useExportContactsApi();

  const handleExport = async () => {
    try {
      await exportContacts();
      toast.success("CSVをダウンロードしました");
    } catch (e) {
      toast.error(
        `エクスポートエラー: ${e instanceof Error ? e.message : "不明なエラー"}`,
      );
    }
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleDelete = async (contactId: string) => {
    setActionLoading(true);
    try {
      await deleteContact(contactId);
      setDeleteConfirm(null);
      await refetch();
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  // CSVインポート関連ハンドラー
  const handleImportModalOpen = () => {
    setImportModalOpen(true);
    setImportFile(null);
    setImportGroupId("");
    setImportResult(null);
  };

  const handleImportModalClose = () => {
    if (!importLoading) {
      setImportModalOpen(false);
      setImportFile(null);
      setImportGroupId("");
      setImportResult(null);
    }
  };

  const handleFileDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file?.name.endsWith(".csv")) {
      setImportFile(file);
      setImportResult(null);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImportFile(file);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    try {
      const result = await importContacts(
        importFile,
        importGroupId || undefined,
      );
      setImportResult(result);
      if (result.imported > 0 || result.updated > 0) {
        await refetch();
      }
    } catch (e) {
      alert(
        `インポートエラー: ${e instanceof Error ? e.message : "不明なエラー"}`,
      );
    }
  };

  const handleDeleteAll = async () => {
    try {
      const result = await deleteAllContacts();
      setDeleteAllModalOpen(false);
      toast.success(`${result.deleted}件の連絡先を削除しました`);
      await refetch();
    } catch (e) {
      toast.error(
        `削除エラー: ${e instanceof Error ? e.message : "不明なエラー"}`,
      );
    }
  };

  const totalPages = data ? Math.ceil(data.total / 50) : 1;

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>配信先管理</PageTitle>
        <div className="alert alert-error">
          <span>エラーが発生しました: {error.message}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-2">
        <PageTitle>配信先管理</PageTitle>
      </div>
      <DistributionTabs
        active="contacts"
        actions={
          <>
            <div className="dropdown dropdown-end">
              <Button
                type="button"
                tabIndex={0}
                aria-label="管理メニュー"
                variant="ghost"
                size="icon"
              >
                <MoreVertical className="w-4 h-4" />
              </Button>
              <ul className="dropdown-content z-50 menu p-1 shadow-lg bg-white rounded-box w-56 border border-slate-200">
                <li>
                  <button type="button" onClick={handleImportModalOpen}>
                    <Upload className="w-4 h-4" />
                    CSVインポート
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={handleExport}
                    disabled={exportLoading || !data?.total}
                  >
                    {exportLoading ? (
                      <Loading size="xs" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    CSVダウンロード
                  </button>
                </li>
                {data && data.total > 0 && (
                  <>
                    <li className="menu-title px-2 pt-2 pb-0">
                      <span className="text-[10px] uppercase">危険操作</span>
                    </li>
                    <li>
                      <button
                        type="button"
                        onClick={() => setDeleteAllModalOpen(true)}
                        className="text-error"
                      >
                        <Trash2 className="w-4 h-4" />
                        すべての連絡先を削除
                      </button>
                    </li>
                  </>
                )}
              </ul>
            </div>
            <Button
              type="button"
              size="sm"
              onClick={() => navigate("/admin/distribution/contacts/new")}
            >
              + 新規作成
            </Button>
          </>
        }
      />

      {/* フィルター */}
      <div className="mb-6 flex flex-wrap gap-2">
        <input
          type="text"
          placeholder="会社名・担当者名・メールで検索..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleSearch()}
          className="input input-bordered input-sm w-full md:w-72"
        />
        <select
          value={groupFilter}
          onChange={e => {
            setGroupFilter(e.target.value);
            setPage(1);
          }}
          className="select select-bordered select-sm"
        >
          <option value="">全てのグループ</option>
          {groupsData?.items.map(group => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </select>
        <select
          value={activeFilter === undefined ? "" : String(activeFilter)}
          onChange={e => {
            const val = e.target.value;
            setActiveFilter(val === "" ? undefined : val === "true");
            setPage(1);
          }}
          className="select select-bordered select-sm"
        >
          <option value="">全ての状態</option>
          <option value="true">有効のみ</option>
          <option value="false">無効のみ</option>
        </select>
        <Button type="button" onClick={handleSearch}>
          検索
        </Button>
      </div>

      {/* テーブル */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <Table.Root density="compact" zebra stickyHeader>
          <Table.Head sticky>
            <tr>
              <Table.HeaderCell>会社名</Table.HeaderCell>
              <Table.HeaderCell>担当者名</Table.HeaderCell>
              <Table.HeaderCell>メールアドレス</Table.HeaderCell>
              <Table.HeaderCell>状態</Table.HeaderCell>
              <Table.HeaderCell>所属グループ</Table.HeaderCell>
              <Table.HeaderCell>操作</Table.HeaderCell>
            </tr>
          </Table.Head>
          <Table.Body>
            {loading ? (
              <tr>
                <td colSpan={6} className="text-center py-8">
                  <Loading size="md" />
                </td>
              </tr>
            ) : data?.items.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="text-center py-8 text-base-content/60"
                >
                  連絡先がありません
                </td>
              </tr>
            ) : (
              data?.items.map(contact => (
                <Table.Row key={contact.id}>
                  <Table.Cell>
                    <Link to={`/admin/distribution/contacts/${contact.id}`}>
                      {contact.companyName}
                    </Link>
                  </Table.Cell>
                  <Table.Cell>{contact.contactName || "-"}</Table.Cell>
                  <Table.Cell>{contact.email}</Table.Cell>
                  <Table.Cell>
                    <StatusBadge
                      tone={contact.isActive ? "soft-success" : "soft-neutral"}
                    >
                      {contact.isActive ? "有効" : "無効"}
                    </StatusBadge>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex flex-wrap gap-1">
                      {contact.groups.slice(0, 3).map(group => (
                        <StatusBadge
                          key={group.id}
                          tone={
                            group.isDefault ? "soft-warning" : "tag-neutral"
                          }
                        >
                          {group.name}
                        </StatusBadge>
                      ))}
                      {contact.groups.length > 3 && (
                        <StatusBadge tone="soft-neutral">
                          +{contact.groups.length - 3}
                        </StatusBadge>
                      )}
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="xs"
                        onClick={() =>
                          navigate(
                            `/admin/distribution/contacts/${contact.id}/edit`,
                          )
                        }
                      >
                        編集
                      </Button>
                      <Button
                        type="button"
                        onClick={() => setDeleteConfirm(contact.id)}
                        variant="destructive"
                        size="xs"
                      >
                        削除
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </div>

      {/* ページネーション */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <div className="join">
            <Button
              type="button"
              size="sm"
              isJoinItem
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              «
            </Button>
            <Button type="button" size="sm" isJoinItem>
              {page} / {totalPages}
            </Button>
            <Button
              type="button"
              size="sm"
              isJoinItem
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              »
            </Button>
          </div>
        </div>
      )}

      {/* 削除確認モーダル */}
      {deleteConfirm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">連絡先を削除しますか？</h3>
            <p className="py-4">この操作は取り消せません。</p>
            <div className="modal-action">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDeleteConfirm(null)}
                disabled={actionLoading}
              >
                キャンセル
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => handleDelete(deleteConfirm)}
                disabled={actionLoading}
              >
                {actionLoading ? <Loading size="sm" /> : "削除"}
              </Button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !actionLoading && setDeleteConfirm(null)}
          />
        </div>
      )}

      {/* CSVインポートモーダル */}
      {importModalOpen && (
        <div className="modal modal-open">
          <div className="modal-box max-w-lg">
            <h3 className="font-bold text-lg mb-4">連絡先のCSVインポート</h3>

            {!importResult ? (
              <>
                {/* ファイルドロップエリア */}
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    isDragging
                      ? "border-primary bg-primary/10"
                      : "border-base-300 hover:border-primary"
                  }`}
                  onDragOver={e => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleFileDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  {importFile ? (
                    <div>
                      <p className="font-medium text-success">
                        {importFile.name}
                      </p>
                      <p className="text-sm text-base-content/60 mt-1">
                        クリックして別のファイルを選択
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="font-medium">
                        CSVファイルをドラッグ＆ドロップ
                      </p>
                      <p className="text-sm text-base-content/60 mt-1">
                        または クリックしてファイルを選択
                      </p>
                    </div>
                  )}
                </div>

                {/* フォーマット説明 */}
                <div className="mt-4 text-sm text-base-content/70">
                  <p>
                    必須列: <code>company_name</code>, <code>email</code>
                  </p>
                  <p>
                    任意列: <code>contact_name</code>, <code>is_active</code>,{" "}
                    <code>notes</code>
                  </p>
                  <div className="text-sm mt-2">
                    <Link
                      href="/import_csv_template.csv"
                      newTab={false}
                      download
                    >
                      サンプルCSVをダウンロード
                    </Link>
                  </div>
                </div>

                {/* グループ選択 */}
                <div className="form-control mt-4">
                  <label className="label">
                    <span className="label-text">
                      インポート先グループ（任意）
                    </span>
                  </label>
                  <select
                    value={importGroupId}
                    onChange={e => setImportGroupId(e.target.value)}
                    className="select select-bordered select-sm"
                  >
                    <option value="">グループを指定しない</option>
                    {groupsData?.items.map(group => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* アクションボタン */}
                <div className="modal-action">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleImportModalClose}
                    disabled={importLoading}
                  >
                    キャンセル
                  </Button>
                  <Button
                    type="button"
                    onClick={handleImport}
                    disabled={!importFile || importLoading}
                  >
                    {importLoading ? <Loading size="sm" /> : "インポート"}
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* インポート結果 */}
                <div className="space-y-3">
                  <div className="stats stats-vertical lg:stats-horizontal shadow w-full">
                    <div className="stat">
                      <div className="stat-title">新規追加</div>
                      <div className="stat-value text-success">
                        {importResult.imported}
                      </div>
                    </div>
                    <div className="stat">
                      <div className="stat-title">更新</div>
                      <div className="stat-value text-info">
                        {importResult.updated}
                      </div>
                    </div>
                    <div className="stat">
                      <div className="stat-title">スキップ</div>
                      <div className="stat-value text-warning">
                        {importResult.skipped}
                      </div>
                    </div>
                  </div>

                  {importResult.errors.length > 0 && (
                    <div className="alert alert-error">
                      <div>
                        <p className="font-medium">
                          エラー: {importResult.errors.length}件
                        </p>
                        <ul className="text-sm mt-2 list-disc list-inside max-h-32 overflow-y-auto">
                          {importResult.errors.map(err => (
                            <li key={err.row}>
                              行{err.row}: {err.error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>

                <div className="modal-action">
                  <Button type="button" onClick={handleImportModalClose}>
                    閉じる
                  </Button>
                </div>
              </>
            )}
          </div>
          <div className="modal-backdrop" onClick={handleImportModalClose} />
        </div>
      )}

      {/* 全削除確認モーダル */}
      {deleteAllModalOpen && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg text-error">
              すべての連絡先を削除しますか？
            </h3>
            <div className="py-4">
              <p className="text-base-content/80">
                この操作により、<strong>{data?.total}件</strong>
                の連絡先とグループへの関連付けがすべて削除されます。
              </p>
              <p className="text-error mt-2 text-sm font-medium">
                この操作は取り消せません。
              </p>
            </div>
            <div className="modal-action">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDeleteAllModalOpen(false)}
                disabled={deleteAllLoading}
              >
                キャンセル
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleDeleteAll}
                disabled={deleteAllLoading}
              >
                {deleteAllLoading ? <Loading size="sm" /> : "すべて削除"}
              </Button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !deleteAllLoading && setDeleteAllModalOpen(false)}
          />
        </div>
      )}
    </div>
  );
};
